import { ReactNode } from "react";
import {
  bestLabel, formatScalar, isEmptyValue, isScalar, labelizeKey, summarizeValue,
} from "../format";

/**
 * <NestedValue> — product-grade rendering for complex bound values.
 * The renderer-wide "no raw JSON" rule lives here:
 *
 *   null / "" / [] / {}      → muted em-dash
 *   scalar                   → plain text (booleans as Yes/No)
 *   array of scalars         → chips (capped, "+n" overflow chip)
 *   array of objects         → count pill + first-item summary
 *   object (depth < 2)       → inline key/value rows, label-cased keys
 *   object (deeper)          → one-line best-label summary
 *
 * Braces and brackets NEVER reach the DOM.
 */

const MAX_CHIPS = 8;
const MAX_KV_ROWS = 10;
const MAX_DEPTH = 2;

export function NestedValue({ value, depth = 0 }: { value: unknown; depth?: number }): ReactNode {
  if (isEmptyValue(value)) return <span className="a2-empty">—</span>;

  if (isScalar(value)) return <>{formatScalar(value)}</>;

  if (Array.isArray(value)) {
    if (value.every(isScalar)) {
      const shown = value.slice(0, MAX_CHIPS);
      const extra = value.length - shown.length;
      return (
        <span className="a2-chips">
          {shown.map((v, i) => (
            <span className="a2-chip" key={i}>{formatScalar(v)}</span>
          ))}
          {extra > 0 && <span className="a2-chip a2-chip-more">+{extra}</span>}
        </span>
      );
    }
    // Array of records (or mixed): count + first-item summary.
    const first = value[0];
    const summary =
      first && typeof first === "object" && !Array.isArray(first)
        ? bestLabel(first as Record<string, unknown>)
        : summarizeValue(first);
    return (
      <span className="a2-collection">
        <span className="a2-count">{value.length}</span>
        <span className="a2-collection-summary">
          {value.length === 1 ? "item" : "items"} · {summary}
        </span>
      </span>
    );
  }

  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    if (depth >= MAX_DEPTH) {
      return <span className="a2-obj-summary">{bestLabel(obj)}</span>;
    }
    const entries = Object.entries(obj);
    const shown = entries.slice(0, MAX_KV_ROWS);
    const extra = entries.length - shown.length;
    return (
      <div className="a2-kv">
        {shown.map(([k, v]) => (
          <div className="a2-kv-row" key={k}>
            <span className="a2-kv-key">{labelizeKey(k)}</span>
            <span className="a2-kv-val"><NestedValue value={v} depth={depth + 1} /></span>
          </div>
        ))}
        {extra > 0 && (
          <div className="a2-kv-row">
            <span className="a2-kv-key a2-kv-more">+{extra} more</span>
            <span className="a2-kv-val" />
          </div>
        )}
      </div>
    );
  }

  return <>{String(value)}</>;
}
